import React from 'react';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import {
  Box,
  Chip,
  Paper,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import type { SafetyControlReviewViewModel } from '../../domain/reportContext/safetyControlReview';

type Props = {
  alignment: SafetyControlReviewViewModel | null;
  title?: string;
  compact?: boolean;
  testId?: string;
};

function renderList(items: string[], limit: number): string {
  const trimmed = items.filter(Boolean).slice(0, limit);
  return trimmed.length > 0 ? trimmed.join(', ') : 'Not recorded';
}

const SafetyControlReviewCard: React.FC<Props> = ({
  alignment,
  title = 'Safety Control Review',
  compact = false,
  testId = 'safety-control-review-card',
}) => {
  if (!alignment) return null;

  const detailRows = [
    { label: 'Control objective', value: alignment.controlObjective || 'Not recorded' },
    { label: 'Primary control lens', value: alignment.primaryControlLens || 'Not recorded' },
    { label: 'Verification status', value: alignment.verificationStatusLabel || 'Not recorded' },
    {
      label: 'Controls to verify',
      value: renderList(alignment.existingControlsToVerify, compact ? 3 : 4),
    },
    {
      label: 'Potential control gaps',
      value: renderList(alignment.potentialControlGaps, compact ? 2 : 3),
    },
    {
      label: 'Field verification questions',
      value: renderList(alignment.fieldVerificationQuestions, compact ? 2 : 3),
    },
    {
      label: 'Closure gate',
      value: renderList(alignment.closureRequirements, compact ? 2 : 3),
    },
  ];

  return (
    <Paper
      variant="outlined"
      sx={{ p: compact ? 1.5 : 2, borderRadius: 3 }}
      data-testid={testId}
    >
      <Stack spacing={compact ? 1 : 1.25}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
          <Typography variant={compact ? 'subtitle2' : 'subtitle1'} sx={{ fontWeight: 700 }}>
            {title}
          </Typography>
          <Tooltip title={alignment.claimBoundary || 'Claim boundary not recorded'}>
            <InfoOutlinedIcon fontSize="small" color="action" />
          </Tooltip>
          <Chip size="small" variant="outlined" label={alignment.sourceLabel || 'Curated mapping'} />
          {alignment.verificationStatus === 'future_template' ? (
            <Chip size="small" variant="outlined" label="Future template" />
          ) : null}
        </Box>
        <Typography variant="body2" color="text.secondary">
          {alignment.uxSummary || alignment.reportSummary || 'Claim-safe control review mapping available.'}
        </Typography>
        {alignment.observedExposure ? (
          <Typography variant="body2" color="text.secondary">
            Observed exposure: {alignment.observedExposure}
          </Typography>
        ) : null}
        <Box
          sx={{
            display: 'grid',
            gap: 1,
            gridTemplateColumns: { xs: '1fr', md: compact ? '1fr' : 'repeat(2, minmax(0, 1fr))' },
          }}
        >
          {detailRows.map((row) => (
            <Box key={row.label}>
              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700 }}>
                {row.label}
              </Typography>
              <Typography variant="body2" sx={{ mt: 0.25 }}>
                {row.value}
              </Typography>
            </Box>
          ))}
        </Box>
        <Typography variant="caption" color="text.secondary">
          Effectiveness boundary: {alignment.effectivenessBoundary || 'Not recorded'}
        </Typography>
      </Stack>
    </Paper>
  );
};

export default SafetyControlReviewCard;
